import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Monitor, ChevronLeft, ChevronRight, Smartphone, X } from 'lucide-react';
import { ScreenService } from '@/utils/services/ScreenService.js';

// Map animation option values to CSS classes
const ANIMATION_CLASS = {
  'fadein': 'fade-in',
  'zoomin': 'zoom-in',
  'ltr': 'slide-left',
  'rtl': 'slide-right',
  'ttb': 'slide-down',
  'btt': 'slide-up',
  'None': '',
  '': '',
};

const SLIDE_IN_ANIMATION_CLASS = {
  'ltr': 'slide-in-ltr',
  'rtl': 'slide-in-rtl',
  'ttb': 'slide-in-ttb',
  'btt': 'slide-in-btt',
};

const DEFAULT_INTERVAL = 2.5; // seconds

/**
 * ScreenPreviewModal
 * @param {object} props
 * @param {object} props.screen - The screen object to preview
 * @param {array} props.devices - All user devices (filter for assigned)
 * @param {function} props.onClose - Close handler
 */
const ScreenPreviewModal = ({ screen, devices, onClose }) => {
  // Slideshow state
  const [slideIndex, setSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [prevSlideIndex, setPrevSlideIndex] = useState(null); // for two-image transitions
  const [bgSlideIndex, setBgSlideIndex] = useState(0); // always the last fully displayed image
  const [videoKey, setVideoKey] = useState(0); // force video reload
  const slideCount = screen.slidesData && screen.slidesData.length;

  // Get current slide animation and duration
  const currentSlide = screen.slidesData && screen.slidesData[slideIndex];
  const animation = currentSlide?.animation || 'None';
  const intervalSec = Number(currentSlide?.duration) || DEFAULT_INTERVAL;

  // Helper: is this a slide-in animation?
  const isSlideIn = ['ltr', 'rtl', 'ttb', 'btt'].includes(animation);

  // Debug which rendering path is taken
  React.useEffect(() => {
    if (isPlaying && getPreviewType() === 'image' && getPreviewUrl()) {
      console.log(`Slide ${slideIndex}: isSlideIn=${isSlideIn}, animation=${animation}, prevSlideIndex=${prevSlideIndex}`);
    }
  }, [slideIndex, isSlideIn, animation, prevSlideIndex, isPlaying]);

  // Simple CSS animations without rotation
  const generateAnimationStyles = () => {
    const styles = `
      .fade-in {
        animation: fadeIn 2s;
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .zoom-in {
        animation: zoomIn 2s;
      }
      @keyframes zoomIn {
        from { 
          transform: scale(0.95); 
          opacity: 0; 
        }
        to { 
          transform: scale(1); 
          opacity: 1; 
        }
      }

      .slide-left {
        animation: slideLeft 2s;
      }
      @keyframes slideLeft {
        from { transform: translateX(-40px); }
        to { transform: translateX(0px); }
      }

      .slide-right {
        animation: slideRight 2s;
      }
      @keyframes slideRight {
        from { transform: translateX(40px); }
        to { transform: translateX(0px); }
      }

      .slide-up {
        animation: slideUp 2s;
      }
      @keyframes slideUp {
        from { transform: translateY(40px); }
        to { transform: translateY(0px); }
      }

      .slide-down {
        animation: slideDown 2s;
      }
      @keyframes slideDown {
        from { transform: translateY(-40px); }
        to { transform: translateY(0px); }
      }

      .slide-in-ltr {
        animation: slideInLTR 2s forwards;
      }
      @keyframes slideInLTR {
        from { transform: translateX(-100%); }
        to { transform: translateX(0%); }
      }

      .slide-in-rtl {
        animation: slideInRTL 2s forwards;
      }
      @keyframes slideInRTL {
        from { transform: translateX(100%); }
        to { transform: translateX(0%); }
      }

      .slide-in-ttb {
        animation: slideInTTB 2s forwards;
      }
      @keyframes slideInTTB {
        from { transform: translateY(-100%); }
        to { transform: translateY(0%); }
      }

      .slide-in-btt {
        animation: slideInBTT 2s forwards;
      }
      @keyframes slideInBTT {
        from { transform: translateY(100%); }
        to { transform: translateY(0%); }
      }
    `;

    return styles;
  };

  // Inject dynamic styles
  React.useEffect(() => {
    const styleId = 'slideshow-animation-styles';
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    styleElement.textContent = generateAnimationStyles();
    
    return () => {
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  // Track background slide index changes
  React.useEffect(() => {
    console.log(`Background slide index changed to: ${bgSlideIndex}, angle: ${screen.angle}`);
  }, [bgSlideIndex, screen.angle]);

  // Track prevSlideIndex changes
  React.useEffect(() => {
    console.log(`prevSlideIndex changed to: ${prevSlideIndex}`);
  }, [prevSlideIndex]);

  // Updated animation class mapping without rotation
  const ANIMATION_CLASS_ROTATED = {
    'fadein': 'fade-in',
    'zoomin': 'zoom-in',
    'ltr': 'slide-left',
    'rtl': 'slide-right',
    'ttb': 'slide-down',
    'btt': 'slide-up',
    'None': '',
    '': '',
  };

  const SLIDE_IN_ANIMATION_CLASS_ROTATED = {
    'ltr': 'slide-in-ltr',
    'rtl': 'slide-in-rtl',
    'ttb': 'slide-in-ttb',
    'btt': 'slide-in-btt',
  };

  // Slideshow autoplay effect with per-slide timing
  React.useEffect(() => {
    if (!isPlaying || !slideCount || slideCount < 2) return;
    setTransitioning(true);
    setPrevSlideIndex(slideIndex);
    const timeout = setTimeout(() => {
      setTransitioning(false);
      setPrevSlideIndex(null);
      setSlideIndex(i => {
        const next = (i === slideCount - 1 ? 0 : i + 1);
        return next;
      });
    }, intervalSec * 1000);
    return () => clearTimeout(timeout);
  }, [isPlaying, slideCount, slideIndex, intervalSec]);

  // Filter devices assigned to this screen
  const assignedDevices = devices.filter(device => device.screenId === screen.key);

  // Helper: get preview image/video for a given slide index
  const getPreviewUrlFor = idx => {
    if (screen.slidesData && screen.slidesData.length > 0) {
      const slide = screen.slidesData[idx];
      if (!slide) return null;
      if (slide.videoUrl) {
        // Add cache-busting for video URLs
        const separator = slide.videoUrl.includes('?') ? '&' : '?';
        return slide.videoUrl + separator + 't=' + Date.now();
      }
      return ScreenService.getThumbnail(
        slide.imageUniversalUrl || slide.imageUrl || slide.thumbUniversalUrl || slide.thumbUrl || slide.videoUrl || null,
        true
      );
    }
    // Single video fallback
    if (screen.videoUrl) {
      // Add cache-busting for video URLs
      const separator = screen.videoUrl.includes('?') ? '&' : '?';
      return screen.videoUrl + separator + 't=' + Date.now();
    }
    // Single image fallback
    if (screen.imageUniversalUrl || screen.imageUrl) {
      return ScreenService.getThumbnail(
        screen.imageUniversalUrl || screen.imageUrl,
        true
      );
    }
    return null;
  };
  // Helper: get preview image/video for the current slide
  const getPreviewUrl = () => getPreviewUrlFor(slideIndex);

  // Helper: get preview type
  const getPreviewType = () => {
    if (screen.slidesData && screen.slidesData.length > 0) {
      const slide = screen.slidesData[slideIndex];
      if (slide.videoUrl) return 'video';
      return 'image';
    }
    if (screen.videoUrl) return 'video';
    if (screen.imageUniversalUrl || screen.imageUrl) return 'image';
    return 'image';
  };

  // Debug logging
  React.useEffect(() => {
    console.log('ScreenPreviewModal Debug:', {
      screenName: screen.name,
      screenType: getPreviewType(),
      previewUrl: getPreviewUrl(),
      videoUrl: screen.videoUrl,
      slidesData: screen.slidesData?.length || 0,
      slideIndex,
      slideCount
    });
  }, [screen, slideIndex]);

  // Force video reload when screen data changes
  React.useEffect(() => {
    if (getPreviewType() === 'video') {
      setVideoKey(prev => prev + 1);
      console.log('Forcing video reload due to screen data change');
    }
  }, [screen.videoUrl, screen.slidesData]);



  // Slideshow navigation
  const prevSlide = React.useCallback(() => {
    if (transitioning) return;
    setTransitioning(true);
    setPrevSlideIndex(slideIndex);
    setTimeout(() => {
      setTransitioning(false);
      setPrevSlideIndex(null);
      setSlideIndex(i => {
        const next = (i === 0 ? slideCount - 1 : i - 1);
        return next;
      });
    }, 300);
  }, [transitioning, slideCount, slideIndex]);

  const nextSlide = React.useCallback(() => {
    if (transitioning) return;
    setTransitioning(true);
    setPrevSlideIndex(slideIndex);
    setTimeout(() => {
      setTransitioning(false);
      setPrevSlideIndex(null);
      setSlideIndex(i => {
        const next = (i === slideCount - 1 ? 0 : i + 1);
        return next;
      });
    }, 300);
  }, [transitioning, slideCount, slideIndex]);

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  // Device online status (lastAccess < 6 min)
  const isOnline = device => device.lastAccess && (Date.now() - device.lastAccess < 360000);

  // Prevent background scroll when modal is open
  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 flex items-center justify-center">
      <div className="relative bg-white rounded-xl shadow-2xl w-[90vw] max-w-7xl h-[80vh] max-h-[85vh] flex overflow-hidden flex-col">
        {/* Header Bar */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 bg-white/95">
          <div className="flex items-center gap-4">
            <span className="font-bold text-2xl text-gray-900 truncate max-w-[40vw]">{screen.name}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Play/Pause button in header if slideshow */}
            {slideCount > 1 && (
              <button
                onClick={() => setIsPlaying(p => !p)}
                className="px-5 py-2 rounded-lg bg-yellow-500 text-white font-semibold shadow-md hover:bg-yellow-600 transition-colors"
                aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
              >
                {isPlaying ? 'PAUSE' : 'PLAY'}
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors"
              aria-label="Close preview"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        {/* Main Content: Preview + Devices */}
        <div className="flex flex-1 min-h-0">
          {/* Left: Preview */}
          <div className="flex-1 flex flex-col items-center justify-center relative" style={{ background: 'transparent' }}>
            {/* Preview content with transition */}
            <div className="flex items-center justify-center w-full h-full relative">
              {/* TV Frame */}
              {getPreviewType() === 'image' && getPreviewUrl() ? (
                // Landscape TV Frame (0° and 180°)
                (screen.angle === 0 || screen.angle === 180 || !screen.angle) ? (
                  <div 
                    className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                    style={{
                      borderBottomWidth: (screen.angle === 0 || !screen.angle) ? '20px' : '2px',
                      borderRightWidth: '2px',
                      borderTopWidth: screen.angle === 180 ? '20px' : '2px',
                      borderLeftWidth: '2px',
                      width: '96%',
                      aspectRatio: '16/9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                      background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                      borderColor: '#1f2937',
                      borderStyle: 'solid'
                    }}
                  >
                    <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                      {/* Background image - always show the previous slide during transitions */}
                      {getPreviewType() === 'image' && getPreviewUrlFor(bgSlideIndex) && (
                        <img
                          src={getPreviewUrlFor(bgSlideIndex)}
                          alt={screen.name}
                          className="absolute inset-0 w-full h-full object-contain rounded-lg"
                          style={{ 
                            zIndex: 0,
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                          key={`bg-${bgSlideIndex}`}
                          onLoad={() => {
                            console.log(`Background image loaded: slide ${bgSlideIndex}`);
                          }}
                        />
                      )}
                      {/* For slide-in animations, render the sliding image on top during transition (only when playing) */}
                      {isPlaying && isSlideIn && prevSlideIndex !== null && getPreviewType() === 'image' && getPreviewUrl() && (
                        <img
                          src={getPreviewUrl()}
                          alt={screen.name}
                          className={`absolute inset-0 w-full h-full object-contain rounded-lg z-10 ${SLIDE_IN_ANIMATION_CLASS_ROTATED[animation]}`}
                          style={{ 
                            zIndex: 1,
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                          onAnimationEnd={() => { 
                            console.log(`Slide-in animation ended for slide ${slideIndex}, animation: ${animation}`);
                            // Only update background if it's different from current slide
                            if (bgSlideIndex !== slideIndex) {
                              setBgSlideIndex(slideIndex);
                            }
                            // Then wait 500ms before removing the animated image to prevent white flash
                            setTimeout(() => {
                              console.log(`Removing prevSlideIndex for slide ${slideIndex}`);
                              setPrevSlideIndex(null);
                            }, 500);
                          }}
                        />
                      )}
                      {/* For other animations, render only the current image with animation class (only when playing) */}
                      {isPlaying && !isSlideIn && prevSlideIndex !== null && getPreviewType() === 'image' && getPreviewUrl() && (
                        <img
                          src={getPreviewUrl()}
                          alt={screen.name}
                          className={`absolute inset-0 w-full h-full object-contain rounded-lg z-10 ${ANIMATION_CLASS_ROTATED[animation]}${transitioning ? ' transitioning' : ''}`}
                          style={{
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                          onAnimationEnd={() => {
                            console.log(`Regular animation ended for slide ${slideIndex}, animation: ${animation}`);
                            // Only update background if it's different from current slide
                            if (bgSlideIndex !== slideIndex) {
                              setBgSlideIndex(slideIndex);
                            }
                            // Then wait 500ms before removing the animated image to prevent white flash
                            setTimeout(() => {
                              console.log(`Removing prevSlideIndex for slide ${slideIndex}`);
                              setPrevSlideIndex(null);
                            }, 500);
                          }}
                        />
                      )}
                      {/* When not playing, show static current image */}
                      {!isPlaying && getPreviewType() === 'image' && getPreviewUrl() && (
                        <img
                          src={getPreviewUrl()}
                          alt={screen.name}
                          className="w-full h-full object-contain rounded-lg z-10"
                          style={{
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  // Portrait TV Frame (90° and 270°)
                  <div 
                    className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                    style={{
                      borderBottomWidth: '2px',
                      borderRightWidth: screen.angle === 270 ? '20px' : '2px',
                      borderTopWidth: '2px',
                      borderLeftWidth: screen.angle === 90 ? '20px' : '2px',
                      width: '45%',
                      height: '85%',
                      aspectRatio: '9/16',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                      background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                      borderColor: '#1f2937',
                      borderStyle: 'solid'
                    }}
                  >
                    <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                      {/* Background image - always show the previous slide during transitions */}
                      {getPreviewType() === 'image' && getPreviewUrlFor(bgSlideIndex) && (
                        <img
                          src={getPreviewUrlFor(bgSlideIndex)}
                          alt={screen.name}
                          className="absolute inset-0 w-full h-full object-contain rounded-lg"
                          style={{ 
                            zIndex: 0,
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                          key={`bg-${bgSlideIndex}`}
                          onLoad={() => {
                            console.log(`Background image loaded: slide ${bgSlideIndex}`);
                          }}
                        />
                      )}
                      {/* For slide-in animations, render the sliding image on top during transition (only when playing) */}
                      {isPlaying && isSlideIn && prevSlideIndex !== null && getPreviewType() === 'image' && getPreviewUrl() && (
                        <img
                          src={getPreviewUrl()}
                          alt={screen.name}
                          className={`absolute inset-0 w-full h-full object-contain rounded-lg z-10 ${SLIDE_IN_ANIMATION_CLASS_ROTATED[animation]}`}
                          style={{ 
                            zIndex: 1,
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                          onAnimationEnd={() => { 
                            console.log(`Slide-in animation ended for slide ${slideIndex}, animation: ${animation}`);
                            // Only update background if it's different from current slide
                            if (bgSlideIndex !== slideIndex) {
                              setBgSlideIndex(slideIndex);
                            }
                            // Then wait 500ms before removing the animated image to prevent white flash
                            setTimeout(() => {
                              console.log(`Removing prevSlideIndex for slide ${slideIndex}`);
                              setPrevSlideIndex(null);
                            }, 500);
                          }}
                        />
                      )}
                      {/* For other animations, render only the current image with animation class (only when playing) */}
                      {isPlaying && !isSlideIn && prevSlideIndex !== null && getPreviewType() === 'image' && getPreviewUrl() && (
                        <img
                          src={getPreviewUrl()}
                          alt={screen.name}
                          className={`absolute inset-0 w-full h-full object-contain rounded-lg z-10 ${ANIMATION_CLASS_ROTATED[animation]}${transitioning ? ' transitioning' : ''}`}
                          style={{
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                          onAnimationEnd={() => {
                            console.log(`Regular animation ended for slide ${slideIndex}, animation: ${animation}`);
                            // Only update background if it's different from current slide
                            if (bgSlideIndex !== slideIndex) {
                              setBgSlideIndex(slideIndex);
                            }
                            // Then wait 500ms before removing the animated image to prevent white flash
                            setTimeout(() => {
                              console.log(`Removing prevSlideIndex for slide ${slideIndex}`);
                              setPrevSlideIndex(null);
                            }, 500);
                          }}
                        />
                      )}
                      {/* When not playing, show static current image */}
                      {!isPlaying && getPreviewType() === 'image' && getPreviewUrl() && (
                        <img
                          src={getPreviewUrl()}
                          alt={screen.name}
                          className="w-full h-full object-contain rounded-lg z-10"
                          style={{
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                        />
                      )}
                    </div>
                  </div>
                )
              ) : (
                // Video content with TV frame
                getPreviewType() === 'video' && getPreviewUrl() ? (
                  // Landscape TV Frame for videos (0° and 180°)
                  (screen.angle === 0 || screen.angle === 180 || !screen.angle) ? (
                    <div 
                      className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                      style={{
                        borderBottomWidth: (screen.angle === 0 || !screen.angle) ? '20px' : '2px',
                        borderRightWidth: '2px',
                        borderTopWidth: screen.angle === 180 ? '20px' : '2px',
                        borderLeftWidth: '2px',
                        width: '600px',
                        height: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                        background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                        borderColor: '#1f2937',
                        borderStyle: 'solid'
                      }}
                    >
                      <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                        <video
                          key={`${getPreviewUrl()}-${videoKey}`}
                          src={getPreviewUrl()}
                          controls

                          className="w-full h-full object-contain rounded-lg"
                          style={{
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    // Portrait TV Frame for videos (90° and 270°)
                    <div 
                      className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                      style={{
                        borderBottomWidth: '2px',
                        borderRightWidth: screen.angle === 270 ? '20px' : '2px',
                        borderTopWidth: '2px',
                        borderLeftWidth: screen.angle === 90 ? '20px' : '2px',
                        width: '400px',
                        height: '600px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                        background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                        borderColor: '#1f2937',
                        borderStyle: 'solid'
                      }}
                    >
                      <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                        <video
                          key={`${getPreviewUrl()}-${videoKey}`}
                          src={getPreviewUrl()}
                          controls

                          className="w-full h-full object-contain rounded-lg"
                          style={{
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                        />
                      </div>
                    </div>
                  )
                ) : (
                  // Fallback content
                  <div className="relative w-full" style={{ aspectRatio: '16/9', maxHeight: '40vh' }}>
                    <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Monitor className="w-20 h-20 mb-4" />
                      <span>No preview available</span>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          {/* Right: Device List */}
          <div className="w-[350px] max-w-[40%] h-full bg-white border-l border-gray-100 flex flex-col">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Assigned Devices</h2>
              <p className="text-gray-500 text-sm">Devices showing this screen</p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {assignedDevices.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <Smartphone className="w-10 h-10 mb-2" />
                  <span>No devices assigned</span>
                </div>
              ) : (
                <ul className="space-y-4">
                  {assignedDevices.map(device => (
                    <li key={device.key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Monitor className="w-6 h-6 text-yellow-600" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{device.name}</div>
                        <div className="text-xs text-gray-500">{device.manufacturer} {device.model}</div>
                      </div>
                      <div className={`text-xs font-bold px-2 py-1 rounded-full ${isOnline(device) ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                        {isOnline(device) ? 'Online' : 'Offline'}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ScreenPreviewModal; 